import type { CookieService } from "@packages/common";
import { COOKIE_SERVICE, PhoneFormat } from "@packages/common";
import type { Config } from "@packages/config";
import { CONFIG } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import type { CookieFormData, FormData } from "./dtos";
import { LEAD_FORM_DATA_SERVICE } from "./lead-form-data.types";

/**
 * Service for managing lead form data stored in cookies.
 */
@Injectable(LEAD_FORM_DATA_SERVICE)
export class LeadFormDataService {
  /**
   * Initializes a new instance of the `LeadFormDataService` class.
   * @param cookie - The cookie service for managing encrypted cookie data.
   * @param config - The application configuration containing cookie-related settings.
   */
  constructor(
    @Inject(COOKIE_SERVICE) private readonly cookie: CookieService,
    @Inject(CONFIG) private readonly config: Config
  ) {}

  /**
   * Retrieves and decrypts lead form data from cookies.
   *
   * @returns An object containing lead form data with default values if no data is found.
   */
  get(): FormData {
    const decryptedData = this.cookie.getDecrypted<CookieFormData>(
      this.config.formDataCookieName
    );
    const defaultData = this.getDefaultFormData();

    if (!decryptedData?.Principal) {
      return defaultData;
    }

    const { Principal } = decryptedData;

    return {
      email: Principal?.email ?? defaultData.email,
      name: Principal?.fullName ?? defaultData.name,
      message: Principal?.message ?? defaultData.message,
      newsletter: Principal?.newsletter ?? defaultData.newsletter,
      phoneNumber: Principal?.numberPhone ?? defaultData.phoneNumber,
      formattedPhoneNumber: Principal?.numberPhone
        ? PhoneFormat.clean(Principal.numberPhone)
        : defaultData.formattedPhoneNumber,
    };
  }

  /**
   * Provides default values for `FormData` when no data is found in cookies.
   *
   * @returns The default `FormData` object.
   */
  private getDefaultFormData(): FormData {
    return {
      email: null,
      name: null,
      message: null,
      newsletter: false,
      phoneNumber: null,
      formattedPhoneNumber: null,
    };
  }
}
